import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

interface BusinessDropdownProps {
  id?: string;
  value: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
  disabled?: boolean;
  dropUp?: boolean;
  "aria-label"?: string;
}

function isSpaceKey(e: React.KeyboardEvent): boolean {
  return e.code === "Space" || e.key === " " || e.key === "Spacebar";
}

export function BusinessDropdown({ id, value, options, onChange, disabled = false, dropUp = false, "aria-label": ariaLabel }: BusinessDropdownProps) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const selected = options.find((o) => o.value === value)?.label ?? "";
  const selectedIndex = options.findIndex((o) => o.value === value);
  const listboxId = id ? `${id}-listbox` : undefined;

  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
      return;
    }

    const nextIndex = selectedIndex >= 0 ? selectedIndex : 0;
    setActiveIndex(nextIndex);
  }, [open, selectedIndex]);

  useEffect(() => {
    if (!open || activeIndex < 0) return;
    optionRefs.current[activeIndex]?.focus();
  }, [activeIndex, open]);

  // Close when focus leaves the entire dropdown
  function handleBlur(e: React.FocusEvent<HTMLDivElement>) {
    const nextTarget = e.relatedTarget;

    if (!(nextTarget instanceof Node) || !ref.current?.contains(nextTarget)) {
      setOpen(false);
    }
  }

  function closeDropdown() {
    setOpen(false);
    buttonRef.current?.focus();
  }

  function selectOption(index: number) {
    const option = options[index];
    if (!option) return;
    onChange(option.value);
    closeDropdown();
  }

  function moveActiveIndex(nextIndex: number) {
    if (options.length === 0) return;
    const boundedIndex = Math.max(0, Math.min(nextIndex, options.length - 1));
    setActiveIndex(boundedIndex);
  }

  function handleTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (disabled || options.length === 0) return;

    if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || isSpaceKey(e)) {
      e.preventDefault();
      const defaultIndex = selectedIndex >= 0 ? selectedIndex : 0;
      setOpen(true);
      setActiveIndex(e.key === "ArrowUp" ? options.length - 1 : defaultIndex);
    }
  }

  function handleOptionKeyDown(e: React.KeyboardEvent<HTMLLIElement>, index: number) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        moveActiveIndex(index + 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        moveActiveIndex(index - 1);
        break;
      case "Home":
        e.preventDefault();
        moveActiveIndex(0);
        break;
      case "End":
        e.preventDefault();
        moveActiveIndex(options.length - 1);
        break;
      case "Enter":
        e.preventDefault();
        selectOption(index);
        break;
      case "Escape":
        e.preventDefault();
        closeDropdown();
        break;
      default:
        if (isSpaceKey(e)) {
          e.preventDefault();
          selectOption(index);
        }
        break;
    }
  }

  return (
    <div ref={ref} className="biz-dropdown" id={id} onBlur={handleBlur}>
      <button ref={buttonRef} type="button" className="biz-dropdown__trigger" disabled={disabled}
        aria-haspopup="listbox" aria-expanded={open} aria-label={ariaLabel} aria-controls={listboxId}
        onKeyDown={handleTriggerKeyDown}
        onClick={() => setOpen((v) => !v)}>
        <span className="biz-dropdown__text">{selected}</span>
        <ChevronDown className={`biz-dropdown__chevron${open ? " biz-dropdown__chevron--open" : ""}`} aria-hidden="true" />
      </button>

      {open && (
        <ul id={listboxId} className={`biz-dropdown__list${dropUp ? " biz-dropdown__list--up" : ""}`} role="listbox">
          {options.map((opt, index) => (
            <li key={opt.value} ref={(element) => {
              optionRefs.current[index] = element;
            }} role="option" aria-selected={opt.value === value}
              tabIndex={activeIndex === index ? 0 : -1}
              className={`biz-dropdown__option${opt.value === value ? " biz-dropdown__option--active" : ""}`}
              onFocus={() => setActiveIndex(index)}
              onKeyDown={(e) => handleOptionKeyDown(e, index)}
              onMouseDown={() => { selectOption(index); }}>
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

